import { AttachmentCategory, type AttachmentSelectDto, AttachmentType } from '@@types/api.types';
import { IconButton } from '@components/core/icon-button.component';
import { ExternalLink } from '@components/shared/external-link.component';
import { FormLabel } from '@components/shared/form-label.component';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@components/ui/accordion';
import { Badge } from '@components/ui/badge';
import { cn } from '@lib/utils';
import { isImageContentType, resolveAttachmentOpenUrl, resolveDownloadFileName } from '@utils/attachment-download.util';
import { DownloadIcon, EditIcon, ExternalLinkIcon, FileIcon, LinkIcon } from 'lucide-react';
import type { FunctionComponent, ReactNode } from 'react';

type AttachmentsListProps = {
  attachments: AttachmentSelectDto[];
  className?: string;
  disableFileDownload?: boolean;
  disableLinkNavigation?: boolean;
  onEdit?: (attachment: AttachmentSelectDto) => void;
  onRowClick?: (attachment: AttachmentSelectDto) => void;
  renderActions?: (attachment: AttachmentSelectDto) => ReactNode;
  showHeader?: boolean;
  showInAccordion?: boolean;
  title?: string;
  useFormLabel?: boolean;
};

const getAttachmentSubtitle = (attachment: AttachmentSelectDto): string | undefined => {
  const description = attachment.description?.trim();

  if (description) {
    return description;
  }

  if (attachment.type === AttachmentType.URL) {
    return attachment.url?.trim();
  }

  return undefined;
};

type AttachmentThumbnailProps = {
  attachment: AttachmentSelectDto;
  disableFileDownload?: boolean;
  disableLinkNavigation?: boolean;
};

const AttachmentThumbnail: FunctionComponent<AttachmentThumbnailProps> = (props) => {
  const { attachment, disableFileDownload = false, disableLinkNavigation = false } = props;
  const canDownload =
    !disableFileDownload &&
    attachment.type === AttachmentType.FILE &&
    Boolean(attachment.downloadUrl) &&
    !isImageContentType(attachment.contentType);
  const canOpen = !disableLinkNavigation && Boolean(resolveAttachmentOpenUrl(attachment));
  const imageUrl = attachment.inlineUrl ?? attachment.downloadUrl;

  if (attachment.type === AttachmentType.FILE && isImageContentType(attachment.contentType) && imageUrl) {
    return (
      <div className="relative shrink-0 size-12 rounded border border-gray-200 overflow-hidden">
        <img
          alt={attachment.title}
          className="size-full object-cover"
          src={imageUrl}
        />
        {canOpen && (
          <a
            aria-label={`Open ${attachment.title}`}
            className="absolute inset-0 flex items-center justify-center bg-black/45 opacity-0 transition-opacity group-hover:opacity-100"
            href={resolveAttachmentOpenUrl(attachment)}
            onClick={(event) => event.stopPropagation()}
            onKeyDown={(event) => event.stopPropagation()}
            rel="noopener noreferrer"
            target="_blank"
          >
            <ExternalLinkIcon
              className="text-white"
              size={16}
            />
          </a>
        )}
      </div>
    );
  }

  const Icon = attachment.type === AttachmentType.URL ? LinkIcon : FileIcon;

  return (
    <div className="relative shrink-0 size-12 rounded border border-gray-200 bg-gray-50 flex items-center justify-center">
      <Icon
        className="text-gray-400"
        size={20}
      />
      {canDownload && attachment.downloadUrl ? (
        <a
          aria-label={`Download ${attachment.title}`}
          className="absolute inset-0 flex items-center justify-center bg-black/45 opacity-0 transition-opacity group-hover:opacity-100"
          download={resolveDownloadFileName(attachment)}
          href={attachment.downloadUrl}
          onClick={(event) => event.stopPropagation()}
          onKeyDown={(event) => event.stopPropagation()}
        >
          <DownloadIcon
            className="text-white"
            size={16}
          />
        </a>
      ) : canOpen ? (
        <a
          aria-label={`Open ${attachment.title}`}
          className="absolute inset-0 flex items-center justify-center bg-black/45 opacity-0 transition-opacity group-hover:opacity-100"
          href={resolveAttachmentOpenUrl(attachment)}
          onClick={(event) => event.stopPropagation()}
          onKeyDown={(event) => event.stopPropagation()}
          rel="noopener noreferrer"
          target="_blank"
        >
          <ExternalLinkIcon
            className="text-white"
            size={16}
          />
        </a>
      ) : null}
    </div>
  );
};

export const AttachmentsList: FunctionComponent<AttachmentsListProps> = (props) => {
  const {
    attachments,
    className,
    disableFileDownload = false,
    disableLinkNavigation = false,
    onEdit,
    onRowClick,
    renderActions,
    showHeader = true,
    showInAccordion = false,
    title = 'Attachments',
    useFormLabel = false,
  } = props;

  if (attachments.length === 0) {
    return null;
  }

  const attachmentsList = (
    <div className="flex flex-col gap-3">
      {attachments.map((attachment) => {
        const fileDownloadUrl = attachment.type === AttachmentType.FILE ? attachment.downloadUrl : undefined;
        const isFeaturedTechnologyImage =
          attachment.category === AttachmentCategory.TECHNOLOGY_IMAGE && attachment.featured;
        const subtitle = getAttachmentSubtitle(attachment);
        const urlHref = attachment.type === AttachmentType.URL ? attachment.url : undefined;

        return (
          <div
            key={attachment.id}
            className={cn(
              'group w-full min-w-0 overflow-hidden rounded-lg border border-gray-200 p-3 transition-colors',
              onRowClick ? 'cursor-pointer hover:bg-gray-50' : 'hover:bg-gray-50',
            )}
            role={onRowClick ? 'button' : undefined}
            tabIndex={onRowClick ? 0 : undefined}
            onClick={onRowClick ? () => onRowClick(attachment) : undefined}
            onKeyDown={
              onRowClick
                ? (event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      onRowClick(attachment);
                    }
                  }
                : undefined
            }
          >
            <div className="flex items-start gap-3">
              <AttachmentThumbnail
                attachment={attachment}
                disableFileDownload={disableFileDownload}
                disableLinkNavigation={disableLinkNavigation}
              />

              <div className="flex flex-col gap-1 min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2 min-w-0">
                  {urlHref && !disableLinkNavigation ? (
                    <div
                      className="min-w-0"
                      onClick={(event) => event.stopPropagation()}
                      onKeyDown={(event) => event.stopPropagation()}
                    >
                      <ExternalLink
                        className="min-w-0 text-sm font-medium"
                        href={urlHref}
                        showIcon={false}
                        title={urlHref}
                      >
                        {attachment.title}
                      </ExternalLink>
                    </div>
                  ) : (
                    <div className="min-w-0 truncate text-sm font-medium text-gray-600">{attachment.title}</div>
                  )}
                  {isFeaturedTechnologyImage ? (
                    <span className="inline-flex items-center rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-medium text-amber-700">
                      Featured
                    </span>
                  ) : null}
                </div>
                {subtitle ? (
                  <div className="line-clamp-2 text-xs text-gray-500 whitespace-pre-wrap">{subtitle}</div>
                ) : null}
                {!fileDownloadUrl && attachment.type === AttachmentType.FILE && (
                  <div className="text-xs text-gray-400">Upload pending</div>
                )}
              </div>

              {renderActions ? (
                <div
                  className="shrink-0 opacity-0 transition-opacity group-hover:opacity-100"
                  onClick={(event) => event.stopPropagation()}
                  onKeyDown={(event) => event.stopPropagation()}
                >
                  {renderActions(attachment)}
                </div>
              ) : onEdit ? (
                <div
                  className="shrink-0 opacity-0 transition-opacity group-hover:opacity-100"
                  onClick={(event) => event.stopPropagation()}
                >
                  <IconButton
                    ariaLabel="Edit attachment"
                    icon={<EditIcon size={16} />}
                    onClick={() => onEdit(attachment)}
                    size="sm"
                    variant="secondary"
                  />
                </div>
              ) : null}
            </div>
          </div>
        );
      })}
    </div>
  );

  if (!showInAccordion) {
    return (
      <section className={cn('flex flex-col gap-2', className)}>
        {showHeader &&
          (useFormLabel ? (
            <FormLabel label={`${title} (${attachments.length})`} />
          ) : (
            <h3 className="text-sm font-medium text-gray-600">
              {title} ({attachments.length})
            </h3>
          ))}
        {attachmentsList}
      </section>
    );
  }

  return (
    <section className={cn('flex flex-col gap-2', className)}>
      {showHeader ? (
        <Accordion
          type="single"
          collapsible
          className="w-full border border-gray-200 rounded-lg"
        >
          <AccordionItem
            value="attachments"
            className="border-0"
          >
            <AccordionTrigger className="p-2 hover:no-underline">
              <div className="flex items-center gap-2">
                <Badge
                  variant="primary"
                  size="xs"
                  className="px-1.5 min-w-7 h-7 flex items-center justify-center text-sm font-semibold"
                >
                  {attachments.length > 99 ? '99+' : attachments.length}
                </Badge>
                <h3 className="text-sm font-medium text-gray-600">{title}</h3>
              </div>
            </AccordionTrigger>
            <AccordionContent className="pt-1 pb-3 px-2 w-full max-w-full overflow-hidden">
              <div className="w-full max-w-full overflow-hidden">{attachmentsList}</div>
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      ) : (
        attachmentsList
      )}
    </section>
  );
};
